import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  SortableList,
  type DescriptionBoxData,
  type CustomerJourneyMapSection,
  type CustomerJourneyMapSectionOrder,
  type CustomerJourneyMapStage,
} from '@vision/ui/interfaces';

export type JourneyMapMeta = {
  id: string;
  name: string;
  dashboard_id?: string;
  creator?: {
    name?: string;
    surname?: string;
  };
  created_at?: string;
};

export interface JourneyMapStateType {
  journeyMapDetail: JourneyMapMeta;
  stages: SortableList<CustomerJourneyMapStage>;
  sections: CustomerJourneyMapSection[];
  sectionOrder: SortableList<CustomerJourneyMapSectionOrder>;
  descriptionBoxes: SortableList<DescriptionBoxData>;
  isDirty: boolean;
  isHydrated: boolean;
  initialPublishSnapshot: string | null;
}

const createInitialJourneyMapDetail = (id = ''): JourneyMapMeta => ({
  id,
  name: '',
});

const initialState: JourneyMapStateType = {
  journeyMapDetail: createInitialJourneyMapDetail(),
  stages: [],
  sections: [],
  sectionOrder: [],
  descriptionBoxes: [],
  isDirty: false,
  isHydrated: false,
  initialPublishSnapshot: null,
};

export const JourneyMapState = createSlice({
  name: 'JourneyMapState',
  initialState,
  reducers: {
    resetJourneyMapEditorAction: (state, { payload }: PayloadAction<{ id?: string } | undefined>) => {
      const nextId = payload?.id || '';
      Object.assign(state, {
        ...initialState,
        journeyMapDetail: createInitialJourneyMapDetail(nextId),
      });
    },
    setJourneyMapDetailAction: (state, { payload }: PayloadAction<JourneyMapMeta>) => {
      state.journeyMapDetail = payload;
    },
    updateJourneyMapTitleAction: (state, { payload }: PayloadAction<string>) => {
      state.journeyMapDetail.name = payload;
    },
    setJourneyMapStagesAction: (state, { payload }: PayloadAction<SortableList<CustomerJourneyMapStage>>) => {
      state.stages = payload;
    },
    setJourneyMapSectionsAction: (state, { payload }: PayloadAction<CustomerJourneyMapSection[]>) => {
      state.sections = payload;
    },
    setJourneyMapSectionOrderAction: (
      state,
      { payload }: PayloadAction<SortableList<CustomerJourneyMapSectionOrder>>,
    ) => {
      state.sectionOrder = payload;
    },
    setJourneyMapDescriptionBoxesAction: (state, { payload }: PayloadAction<SortableList<DescriptionBoxData>>) => {
      state.descriptionBoxes = payload;
    },
    setJourneyMapIsDirtyAction: (state, { payload }: PayloadAction<boolean>) => {
      state.isDirty = payload;
    },
    setJourneyMapIsHydratedAction: (state, { payload }: PayloadAction<boolean>) => {
      state.isHydrated = payload;
    },
    setJourneyMapInitialPublishSnapshotAction: (state, { payload }: PayloadAction<string | null>) => {
      state.initialPublishSnapshot = payload;
    },
  },
});

export const {
  resetJourneyMapEditorAction,
  setJourneyMapDetailAction,
  updateJourneyMapTitleAction,
  setJourneyMapStagesAction,
  setJourneyMapSectionsAction,
  setJourneyMapSectionOrderAction,
  setJourneyMapDescriptionBoxesAction,
  setJourneyMapIsDirtyAction,
  setJourneyMapIsHydratedAction,
  setJourneyMapInitialPublishSnapshotAction,
} = JourneyMapState.actions;
